import { useScrollLock } from "@/hooks/useScrollLock";
import { cn } from "@/lib/utils";
import {
  globalStateSelector,
  handleCloseDrawer as handleCloseDrawerAction,
  handleOpenDrawer,
  setIsShowingScrolledNavbar,
  topToPageSelector,
} from "@/redux/globalState/globalStateSlice";
import { AppDispatch } from "@/redux/store";
import { Subscription } from "@/redux/user/userSlice";
import { useTranslation } from "@repo/shared/i18n";
import { Menu } from "lucide-react";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Drawer } from "./components/Drawer";

export const NavBarTouchDevice = ({
  currentRoute,
  userIsLoggedIn,
  freeUser,
  paidUserButNotUltimate,
  userSubscription,
}: {
  currentRoute: string;
  userIsLoggedIn: boolean;
  canUseClubImport: boolean;
  freeUser: boolean;
  paidUserButNotUltimate: boolean;
  userSubscription: Subscription;
}) => {
  return (
    <TouchDeviceNavBar
      currentRoute={currentRoute}
      userIsLoggedIn={userIsLoggedIn}
      freeUser={freeUser}
      paidUserButNotUltimate={paidUserButNotUltimate}
      userSubscription={userSubscription}
    />
  );
};

const TouchDeviceNavBar = ({
  currentRoute,
  userIsLoggedIn,
  freeUser,
  paidUserButNotUltimate,
  userSubscription,
}: {
  currentRoute: string;
  userIsLoggedIn: boolean;
  freeUser: boolean;
  paidUserButNotUltimate: boolean;
  userSubscription: Subscription;
}) => {
  const { t } = useTranslation("nav");
  const { isNavbarDrawerOpen } = useSelector(globalStateSelector);
  const dispatch = useDispatch<AppDispatch>();
  const { isShowingScrolledNavbar, touchBarMenuZIndex, isFloatingMenuHidden } =
    useSelector(globalStateSelector);
  const topToPage = useSelector(topToPageSelector);

  useScrollLock(isNavbarDrawerOpen);

  const handleCloseDrawer = () => dispatch(handleCloseDrawerAction());

  const handleShowScrolledNavbar = ({ isScrolled }: { isScrolled: boolean }) =>
    dispatch(setIsShowingScrolledNavbar(isScrolled));

  useEffect(() => {
    const handleScroll = () => {
      handleShowScrolledNavbar({ isScrolled: window.scrollY > 16 });
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      <Drawer
        isDrawerOpen={isNavbarDrawerOpen}
        currentRoute={currentRoute}
        onClose={handleCloseDrawer}
        userIsLoggedIn={userIsLoggedIn}
      />
      <div
        className={cn(
          "fixed right-3 flex flex-col items-center gap-1",
          !isShowingScrolledNavbar && "pointer-events-none",
          touchBarMenuZIndex,
        )}
        style={{ top: topToPage.value + 16 }}
        hidden={isFloatingMenuHidden}
      >
        <button
          aria-label={t("openMenu")}
          className={cn(
            "size-10 rounded-full bg-surface/80 backdrop-blur-md flex items-center justify-center shadow-lg border border-edge-strong transition-all duration-300 ease-out",
            isShowingScrolledNavbar
              ? "opacity-100 scale-100"
              : "opacity-0 scale-50",
          )}
          onClick={(e) => {
            e.stopPropagation();
            dispatch(handleOpenDrawer());
          }}
        >
          <Menu className="size-5 text-foreground-muted" />
        </button>
      </div>
    </>
  );
};
